import { CANVAS_CONFIG, getCanvasConfig, getLadderBounds } from './constants.js';
import { getLoadedImage } from './ladderImages.js';

/**
 * Initialize canvas with high DPI support
 */
export function initializeCanvas(canvas, gameConfig) {
  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;
  const canvasConfig = getCanvasConfig(gameConfig);

  canvas.width = canvasConfig.width * dpr;
  canvas.height = canvasConfig.height * dpr;

  ctx.scale(dpr, dpr);

  return ctx;
}

const LADDER_PALETTES = {
  default: {
    edge: '#9A6208',
    mid: '#E5A020',
    highlight: '#FFE566',
    streak: '#FFF8D0',
    shadow: 'rgba(60, 30, 0, 0.35)',
  },
  active: {
    edge: '#C04800',
    mid: '#FF9220',
    highlight: '#FFD878',
    streak: '#FFF4C8',
    shadow: 'rgba(100, 30, 0, 0.45)',
  },
};

function getLadderPalette(variant = 'default') {
  return LADDER_PALETTES[variant] || LADDER_PALETTES.default;
}

function draw3DVerticalBar(ctx, x, y1, y2, width, palette) {
  const half = width / 2;
  const height = y2 - y1;
  if (height <= 0) return;

  ctx.fillStyle = palette.shadow;
  roundRect(ctx, x - half + 1.5, y1 + 1.5, width, height, half);
  ctx.fill();

  const gradient = ctx.createLinearGradient(x - half, 0, x + half, 0);
  gradient.addColorStop(0, palette.edge);
  gradient.addColorStop(0.32, palette.mid);
  gradient.addColorStop(0.5, palette.highlight);
  gradient.addColorStop(0.68, palette.mid);
  gradient.addColorStop(1, palette.edge);

  ctx.fillStyle = gradient;
  roundRect(ctx, x - half, y1, width, height, half);
  ctx.fill();

  ctx.fillStyle = palette.streak;
  ctx.globalAlpha = 0.45;
  roundRect(ctx, x - half + 1.5, y1 + 3, Math.max(width * 0.22, 2), height - 6, 2);
  ctx.fill();
  ctx.globalAlpha = 1;
}

function draw3DHorizontalBar(ctx, x1, x2, y, height, palette) {
  const width = x2 - x1;
  const half = height / 2;
  if (width <= 0) return;

  ctx.fillStyle = palette.shadow;
  roundRect(ctx, x1 + 1.5, y - half + 2, width, height, half);
  ctx.fill();

  const gradient = ctx.createLinearGradient(0, y - half, 0, y + half);
  gradient.addColorStop(0, palette.highlight);
  gradient.addColorStop(0.42, palette.mid);
  gradient.addColorStop(1, palette.edge);

  ctx.fillStyle = gradient;
  roundRect(ctx, x1, y - half, width, height, half);
  ctx.fill();

  ctx.strokeStyle = palette.streak;
  ctx.globalAlpha = 0.55;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(x1 + half, y - half + 2.5);
  ctx.lineTo(x2 - half, y - half + 2.5);
  ctx.stroke();
  ctx.globalAlpha = 1;
}

/**
 * Draw vertical lines (ladders) with 3D golden bar style
 */
export function drawVerticalLines(ctx, verticalLines, gameConfig, options = {}) {
  const canvasConfig = getCanvasConfig(gameConfig);
  const { ladderTop, ladderBottom } = getLadderBounds(gameConfig);
  const palette = getLadderPalette(options.variant);
  const barWidth = options.width ?? canvasConfig.ladderBarWidth;

  verticalLines.forEach((line) => {
    draw3DVerticalBar(ctx, line.x, ladderTop, ladderBottom, barWidth, palette);
  });
}

const PLAYER_LABEL_HEIGHT = 32;
const PLAYER_LABEL_MIN_WIDTH = 48;
const PLAYER_LABEL_MAX_WIDTH = 160;
const REWARD_CARD_MIN_WIDTH = 48;
const REWARD_CARD_MAX_WIDTH = 100;

/**
 * Get player label layout constants for a given game config
 */
export function getPlayerLabelLayout(gameConfig, assets = null) {
  const canvasConfig = getCanvasConfig(gameConfig);
  const usePlayerImages = Boolean(assets?.playerImgs?.length);
  const height = usePlayerImages ? canvasConfig.playerImgSize : PLAYER_LABEL_HEIGHT;
  const y = canvasConfig.padding + 8 + canvasConfig.playerOffsetY;

  return {
    y,
    height,
    textY: y + height - 4,
  };
}

/**
 * Get clickable bounds for a player label button
 */
export function getPlayerLabelBounds(verticalLines, index, gameConfig, assets = null) {
  const layout = getPlayerLabelLayout(gameConfig, assets);
  const canvasConfig = getCanvasConfig(gameConfig);
  const line = verticalLines[index];
  const spacing = index < verticalLines.length - 1
    ? verticalLines[index + 1].x - line.x
    : line.x - verticalLines[index - 1].x;
  const width = Math.min(Math.max(spacing * 0.85, PLAYER_LABEL_MIN_WIDTH), PLAYER_LABEL_MAX_WIDTH);
  const centerX = line.x + canvasConfig.playerOffsetX;

  return {
    x: line.x - width / 2,
    x: centerX - width / 2,
    y: layout.y,
    width,
    height: layout.height,
    centerX: line.x,
    centerX,
    centerY: layout.y + layout.height / 2,
  };
}

/**
 * Find player index at canvas coordinates (returns null if outside label area)
 */
export function findPlayerIndexAtPoint(x, y, verticalLines, gameConfig, assets = null) {
  const layout = getPlayerLabelLayout(gameConfig, assets);

  if (y < layout.y || y > layout.y + layout.height) {
    return null;
  }

  for (let i = 0; i < verticalLines.length; i++) {
    const bounds = getPlayerLabelBounds(verticalLines, i, gameConfig, assets);
    if (x >= bounds.x && x <= bounds.x + bounds.width) {
      return i;
    }
  }

  return null;
}

/**
 * Convert screen coordinates to canvas logical coordinates
 */
export function getCanvasCoords(canvas, clientX, clientY, gameConfig) {
  const rect = canvas.getBoundingClientRect();
  const canvasConfig = getCanvasConfig(gameConfig);

  return {
    x: (clientX - rect.left) * (canvasConfig.width / rect.width),
    y: (clientY - rect.top) * (canvasConfig.height / rect.height),
  };
}

/**
 * Draw player labels above vertical lines (below card area)
 * @param {Object} options
 * @param {number|null} options.selectedStartIndex - Currently selected player index
 * @param {boolean} options.interactive - Draw as clickable buttons
 * @param {number|null} options.hoveredIndex - Hovered player index
 */
export function drawPlayerLabels(ctx, config, verticalLines, gameConfig, options = {}) {
  const { selectedStartIndex = null, interactive = false, hoveredIndex = null, assets = null } = options;
  const canvasConfig = getCanvasConfig(gameConfig);
  const textColor = getComputedStyle(document.documentElement).getPropertyValue('--text').trim() || '#000';
  const accentColor = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#aa3bff';
  const bgColor = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim() || '#fff';
  const layout = getPlayerLabelLayout(gameConfig, assets);

  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'center';

  verticalLines.forEach((line, index) => {
    const playerName = config.language === 'en' ? config.players[index]?.nameEn : config.players[index]?.name;
    const label = playerName || `${index + 1}`;
    const playerImgUrl = assets?.playerImgs?.[index];
    const playerImg = playerImgUrl ? getLoadedImage(playerImgUrl) : null;

    if (interactive) {
      const bounds = getPlayerLabelBounds(verticalLines, index, gameConfig, assets);
      const isSelected = selectedStartIndex === index;
      const isHovered = hoveredIndex === index;

      if (playerImg) {
        const lift = isSelected ? canvasConfig.playerSelectedLift : 0;

        drawImageFitHeight(ctx, playerImg, bounds.centerX, bounds.centerY, canvasConfig.playerImgSize, {
          clipRect: { x: bounds.x, y: bounds.y - lift, width: bounds.width, height: bounds.height },
          offsetY: lift,
        });
      } else {
        const lift = isSelected ? canvasConfig.playerSelectedLift : 0;

        ctx.fillStyle = isSelected || isHovered ? accentColor : bgColor;
        roundRect(ctx, bounds.x, bounds.y - lift, bounds.width, bounds.height, 8);
        ctx.fill();

        ctx.strokeStyle = accentColor;
        ctx.lineWidth = 2;
        roundRect(ctx, bounds.x, bounds.y - lift, bounds.width, bounds.height, 8);
        ctx.stroke();

        ctx.fillStyle = isSelected || isHovered ? '#fff' : textColor;
        ctx.textBaseline = 'middle';
        ctx.fillText(label, bounds.centerX, bounds.centerY - lift);
      }
    } else if (playerImg) {
      const centerX = line.x + canvasConfig.playerOffsetX;
      const centerY = layout.y + layout.height / 2;
      drawImageFitHeight(ctx, playerImg, centerX, centerY, canvasConfig.playerImgSize);
    } else {
      ctx.fillStyle = textColor;
      ctx.textBaseline = 'bottom';
      ctx.fillText(label, line.x + canvasConfig.playerOffsetX, layout.textY);
    }
  });
}

function getRewardCardWidth(verticalLines, index) {
  const line = verticalLines[index];
  const spacing = index < verticalLines.length - 1
    ? verticalLines[index + 1].x - line.x
    : line.x - verticalLines[index - 1].x;

  return Math.min(Math.max(spacing * 0.85, REWARD_CARD_MIN_WIDTH), REWARD_CARD_MAX_WIDTH);
}

function drawWrappedCardText(ctx, text, x, y, maxWidth, lineHeight = 18) {
  const words = text.split(' ');
  let line = '';
  const lines = [];

  for (const word of words) {
    const testLine = line + word + ' ';
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && line !== '') {
      lines.push(line);
      line = word + ' ';
    } else {
      line = testLine;
    }
  }
  lines.push(line);

  const startY = y - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((textLine, i) => {
    ctx.fillText(textLine.trim(), x, startY + i * lineHeight);
  });
}

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Height-fit dimensions (width scales by aspect ratio, same as background)
 */
function getHeightFitDimensions(img, targetHeight) {
  const scale = targetHeight / img.naturalHeight;
  return {
    drawW: img.naturalWidth * scale,
    drawH: targetHeight,
  };
}

/**
 * Contain-fit dimensions (entire image visible within max box, aspect ratio preserved)
 */
function getContainDimensions(img, maxWidth, maxHeight) {
  const scale = Math.min(maxWidth / img.naturalWidth, maxHeight / img.naturalHeight);
  return {
    drawW: img.naturalWidth * scale,
    drawH: img.naturalHeight * scale,
  };
}

/**
 * Draw image fitted to target height; optional clip rect and vertical offset
 */
function drawImageFitHeight(ctx, img, centerX, centerY, targetHeight, { clipRect = null, offsetY = 0 } = {}) {
  const { drawW, drawH } = getHeightFitDimensions(img, targetHeight);
  const drawX = centerX - drawW / 2;
  const drawY = centerY - drawH / 2 - offsetY;

  if (clipRect) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(clipRect.x, clipRect.y, clipRect.width, clipRect.height);
    ctx.clip();
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
    ctx.restore();
  } else {
    ctx.drawImage(img, drawX, drawY, drawW, drawH);
  }
}

/**
 * Draw image contained within max width/height (no cropping)
 */
function drawImageContain(ctx, img, centerX, centerY, maxWidth, maxHeight) {
  const { drawW, drawH } = getContainDimensions(img, maxWidth, maxHeight);
  ctx.drawImage(img, centerX - drawW / 2, centerY - drawH / 2, drawW, drawH);
}

function createPlaceholderRewards(count) {
  return Array.from({ length: count }, (_, index) => ({
    index,
    label: '',
    value: 0,
  }));
}

function resolveRewards(rewards, shuffledRewards, slotCount) {
  const source = shuffledRewards || rewards;
  if (source?.length) return source;
  return createPlaceholderRewards(slotCount);
}

function drawSingleRewardCard(ctx, { x, cardWidth, reward, rewardCardCenterY, rewardCardHeight, showText, assets = null, gameConfig = null }) {
  const canvasConfig = getCanvasConfig(gameConfig);
  const accentColor = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#aa3bff';
  const bgColor = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim() || '#fff';
  const prizeImg = assets?.prizeImg ? getLoadedImage(assets.prizeImg) : null;
  const cardX = x - cardWidth / 2;
  const cardY = rewardCardCenterY - rewardCardHeight / 2;

  if (prizeImg) {
    drawImageContain(ctx, prizeImg, x, rewardCardCenterY, cardWidth, rewardCardHeight);
  } else {
    ctx.fillStyle = bgColor;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
    ctx.shadowBlur = 6;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 2;

    roundRect(ctx, cardX, cardY, cardWidth, rewardCardHeight, 8);
    ctx.fill();

    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

    ctx.strokeStyle = accentColor;
    ctx.lineWidth = 2;
    roundRect(ctx, cardX, cardY, cardWidth, rewardCardHeight, 8);
    ctx.stroke();
  }

  if (showText && reward?.label) {
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 14px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = canvasConfig.rewardTextShadowColor;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = canvasConfig.rewardTextShadowOffsetY;
    ctx.shadowBlur = canvasConfig.rewardTextShadowBlur;
    drawWrappedCardText(ctx, reward.label, x, rewardCardCenterY, cardWidth - 20);
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }
}

/**
 * Draw reward cards at the bottom of the ladder
 */
export function drawRewardCards(ctx, verticalLines, gameConfig, language, options = {}) {
  const { rewards = null, shuffledRewards = null, showText = true, assets = null } = options;
  const { rewardCardCenterY, rewardCardHeight } = getLadderBounds(gameConfig);
  const rewardList = resolveRewards(rewards, shuffledRewards, verticalLines.length);

  verticalLines.forEach((line, index) => {
    const reward = rewardList[index];
    if (!reward) return;

    drawSingleRewardCard(ctx, {
      x: line.x,
      cardWidth: getRewardCardWidth(verticalLines, index),
      reward,
      rewardCardCenterY,
      rewardCardHeight,
      language,
      showText,
      assets,
      gameConfig,
    });
  });
}

/**
 * Animate reward cards shuffling to their final bottom positions (no text)
 */
export function drawShufflingRewardCards(ctx, verticalLines, gameConfig, language, originalRewards, shuffledRewards, progress, assets = null) {
  const { rewardCardCenterY, rewardCardHeight } = getLadderBounds(gameConfig);
  const eased = easeOutCubic(Math.min(Math.max(progress, 0), 1));

  shuffledRewards.forEach((reward, toIndex) => {
    const fromIndex = originalRewards.findIndex((item) => item.index === reward.index);
    if (fromIndex < 0) return;
    const fromX = verticalLines[fromIndex].x;
    const toX = verticalLines[toIndex].x;
    const x = fromX + (toX - fromX) * eased;

    drawSingleRewardCard(ctx, {
      x,
      cardWidth: getRewardCardWidth(verticalLines, toIndex),
      reward,
      rewardCardCenterY,
      rewardCardHeight,
      language,
      showText: false,
      assets,
      gameConfig,
    });
  });
}

/**
 * Draw horizontal lines (connections) with 3D golden rung style
 */
export function drawHorizontalLines(ctx, horizontalLines, gameConfig, options = {}) {
  const canvasConfig = getCanvasConfig(gameConfig);
  const palette = getLadderPalette(options.variant);
  const rungHeight = options.height ?? canvasConfig.ladderRungHeight;

  horizontalLines.forEach((line) => {
    draw3DHorizontalBar(ctx, line.x1, line.x2, line.y, rungHeight, palette);
  });
}

/**
 * Draw traveled path (지나간 경로) with 3D highlight overlay
 */
export function drawTraveledPath(ctx, path, progress, gameConfig) {
  const canvasConfig = getCanvasConfig(gameConfig);
  const palette = getLadderPalette('active');
  const barWidth = canvasConfig.ladderBarWidth + 1;
  const rungHeight = canvasConfig.ladderRungHeight + 1;

  const currentPathIndex = Math.floor(progress * (path.length - 1));
  if (currentPathIndex < 1) return;

  for (let i = 1; i <= currentPathIndex; i++) {
    const p0 = path[i - 1];
    const p1 = path[i];
    const dx = Math.abs(p1.x - p0.x);
    const dy = Math.abs(p1.y - p0.y);

    if (dx > dy) {
      draw3DHorizontalBar(
        ctx,
        Math.min(p0.x, p1.x),
        Math.max(p0.x, p1.x),
        p0.y,
        rungHeight,
        palette
      );
    } else {
      draw3DVerticalBar(
        ctx,
        p0.x,
        Math.min(p0.y, p1.y),
        Math.max(p0.y, p1.y),
        barWidth,
        palette
      );
    }
  }
}

/**
 * Draw ball/character at current position
 */
export function drawBall(ctx, position, gameConfig, assets = null) {
  const canvasConfig = getCanvasConfig(gameConfig);
  const pointerImg = assets?.pointerImg ? getLoadedImage(assets.pointerImg) : null;

  if (pointerImg) {
    const size = canvasConfig.pointerImgSize;
    ctx.drawImage(
      pointerImg,
      position.x - size / 2,
      position.y - size / 2,
      size,
      size
    );
    return;
  }

  const ballColor = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#aa3bff';
  const radius = canvasConfig.ballRadius;

  // Draw ball with glow effect
  ctx.fillStyle = ballColor;
  ctx.beginPath();
  ctx.arc(position.x, position.y, radius, 0, Math.PI * 2);
  ctx.fill();

  // Draw outer circle (border)
  ctx.strokeStyle = ballColor;
  ctx.lineWidth = 2;
  ctx.stroke();

  // Draw inner highlight
  ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
  ctx.beginPath();
  ctx.arc(position.x - radius / 3, position.y - radius / 3, radius / 3, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * Draw background image fitted to canvas height (width scales by aspect ratio)
 */
function drawBackgroundImage(ctx, bgImg, canvasConfig) {
  const { width: canvasW, height: canvasH } = canvasConfig;
  const { drawW, drawH } = getHeightFitDimensions(bgImg, canvasH);
  const drawX = (canvasW - drawW) / 2;

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, canvasW, canvasH);
  ctx.clip();
  ctx.drawImage(bgImg, drawX, 0, drawW, drawH);
  ctx.restore();
}

/**
 * Clear canvas
 */
export function clearCanvas(ctx, gameConfig, assets = null) {
  const canvasConfig = getCanvasConfig(gameConfig);
  const bgImg = assets?.bg ? getLoadedImage(assets.bg) : null;

  if (bgImg) {
    drawBackgroundImage(ctx, bgImg, canvasConfig);
    return;
  }

  // Clear to transparent
  ctx.clearRect(0, 0, canvasConfig.width, canvasConfig.height);
}

/**
 * Helper function to draw rounded rectangle
 */
function roundRect(ctx, x, y, width, height, radius) {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

/**
 * Render complete game state
 */
export function render(ctx, state, config, language, assets = null) {
  clearCanvas(ctx, config, assets);

  if (state.ladderData.verticalLines.length > 0) {
    drawVerticalLines(ctx, state.ladderData.verticalLines, config);
  }

  if (state.ladderData.horizontalLines.length > 0 && state.rewardsRevealed) {
    drawHorizontalLines(ctx, state.ladderData.horizontalLines, config);
  }

  if (state.animationProgress > 0 && state.ladderData.path.length > 0) {
    const progress = state.animationProgress >= 1 ? 1 : state.animationProgress;
    drawTraveledPath(ctx, state.ladderData.path, progress, config);
  }

  if (state.ladderData.verticalLines.length > 0) {
    drawPlayerLabels(ctx, { ...config, language }, state.ladderData.verticalLines, config, { assets });
    drawRewardCards(
      ctx,
      state.ladderData.verticalLines,
      config,
      language,
      {
        rewards: state.rewards,
        shuffledRewards: state.shuffledRewards,
        showText: state.rewardsRevealed,
        assets,
      }
    );
  }

  if (state.animationProgress > 0 && state.animationProgress < 1 && state.ladderData.path.length > 0) {
    const pathIndex = Math.floor(state.animationProgress * (state.ladderData.path.length - 1));
    const position = state.ladderData.path[pathIndex];
    if (position) {
      drawBall(ctx, position, config, assets);
    }
  }
}